// KARS-KI: Direktlinks aus Antworten prüfen und in Router-Ziele übersetzen.
//
// Das Modell darf nur Ziele nennen, die in der Wissensdatei stehen. Diese
// Datei prüft zusätzlich clientseitig: der Routenname muss existieren und die
// Query darf nur bekannte Schlüssel tragen – sonst bleibt die Beschriftung
// schlichter Text. Ohne Vue-Abhängigkeit, damit Vitest sie direkt testen kann.

import type { RouteLocationRaw } from 'vue-router'

export interface RouteToken {
  name: string
  query: Record<string, string>
}

/**
 * Query-Schlüssel je Seite, die als Deep-Link verstanden werden. Muss mit
 * QUERY_HINTS in scripts/knowledge-build.mjs übereinstimmen (Test:
 * __tests__/assistantLinks.test.ts vergleicht mit der erzeugten Wissensdatei).
 */
export const ALLOWED_QUERY_KEYS: Record<string, string[]> = {
  PageUserSettings: ['abschnitt'],
  PageOrganization: ['bereich', 'abschnitt', 'panel', 'ansicht', 'unternehmen'],
  PageCommunicationSettings: ['bereich', 'gruppe', 'ansicht', 'empfaenger', 'neu'],
  PageCommunication: ['tab'],
  PageWelcomeHub: ['bereich'],
  PageHelp: ['artikel', 'kategorie', 'suche'],
  PageCases: ['action'],
  PageInvoices: ['section'],
}

/** Schlüssel, die jede Seite tragen darf. */
const GLOBAL_QUERY_KEYS = ['from']

/** `PageUserSettings?abschnitt=benachrichtigungen` → { name, query }. */
export function parseRouteToken (target: string): RouteToken | null {
  const trimmed = target.trim()
  const match = /^([A-Za-z][A-Za-z0-9_]*)(?:\?(.*))?$/.exec(trimmed)
  if (!match) {
    return null
  }
  const query: Record<string, string> = {}
  if (match[2]) {
    for (const pair of match[2].split('&')) {
      if (!pair) {
        continue
      }
      const [rawKey, ...rest] = pair.split('=')
      const key = safeDecode(rawKey ?? '')
      const value = safeDecode(rest.join('='))
      if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(key)) {
        return null
      }
      if (!/^[\p{L}\p{N} _.:@-]*$/u.test(value)) {
        return null
      }
      query[key] = value
    }
  }
  return { name: match[1] ?? '', query }
}

function safeDecode (value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  } catch {
    return value
  }
}

export interface RouteCatalog {
  hasRoute: (name: string) => boolean
  /** Pfad der Route (für Parameter wie `:idCase`), sonst undefined. */
  pathOf?: (name: string) => string | undefined
}

/**
 * Prüft ein Token gegen den Router und liefert ein Ziel für router.push –
 * oder null, wenn Route oder Query nicht erlaubt sind.
 */
export function resolveRouteToken (catalog: RouteCatalog, target: string): RouteLocationRaw | null {
  const token = parseRouteToken(target)
  if (!token || !catalog.hasRoute(token.name)) {
    return null
  }

  const path = catalog.pathOf?.(token.name) ?? ''
  const paramNames = [...path.matchAll(/:([A-Za-z0-9_]+)/g)].map(m => m[1] ?? '')
  const allowedQuery = new Set([...(ALLOWED_QUERY_KEYS[token.name] ?? []), ...GLOBAL_QUERY_KEYS])

  const params: Record<string, string> = {}
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(token.query)) {
    if (paramNames.includes(key)) {
      params[key] = value
    } else if (allowedQuery.has(key)) {
      query[key] = value
    } else {
      return null
    }
  }
  for (const p of paramNames) {
    if (!(p in params)) {
      return null
    }
  }

  const location: Record<string, unknown> = { name: token.name }
  if (Object.keys(params).length > 0) {
    location.params = params
  }
  if (Object.keys(query).length > 0) {
    location.query = query
  }
  return location as RouteLocationRaw
}
